import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { reportingErrorStatus } from 'src/queries/reporting/reporting.errors'
import { reportingAgentQueryKeys } from 'src/queries/reportingAgent/reportingAgent.keys'
import {
  fetchReportingDraftDisplay,
  fetchReportingDraftRequest,
  fetchReportingMessages,
  fetchReportingRun,
} from 'src/queries/reportingAgent/reportingAgent.network'

const required = (value: string | undefined, name: string): string => {
  if (!value) throw new Error(`${name} is required`)
  return value
}

interface ReportingAgentQueryScope {
  actorId?: string
  organizationId?: string
  reportingEnabled?: boolean
}

export const reportingAgentQueries = {
  draftRequest: ({
    actorId,
    organizationId,
    draftRequestId,
    reportingEnabled = true,
  }: ReportingAgentQueryScope & {
    draftRequestId?: string
  }) =>
    queryOptions({
      queryKey: reportingAgentQueryKeys.draftRequest(
        actorId,
        organizationId,
        draftRequestId,
      ),
      queryFn: async () => {
        required(actorId, 'actorId')
        try {
          return await fetchReportingDraftRequest({
            organizationId: required(organizationId, 'organizationId'),
            draftRequestId: required(draftRequestId, 'draftRequestId'),
          })
        } catch (error) {
          // A routed request id exists before its first submission. Cache that
          // expected absence as data so the global QueryCache does not report
          // every new composer as a failed request.
          if (reportingErrorStatus(error) === 404) return null
          throw error
        }
      },
      enabled: Boolean(
        reportingEnabled && actorId && organizationId && draftRequestId,
      ),
      retry: (failureCount, error) => {
        const status = reportingErrorStatus(error)
        const isClientError =
          status !== undefined && status >= 400 && status < 500
        return !isClientError && failureCount < 2
      },
    }),
  messages: ({
    actorId,
    organizationId,
    conversationId,
    reportingEnabled = true,
  }: ReportingAgentQueryScope & {
    conversationId?: string
  }) =>
    infiniteQueryOptions({
      queryKey: reportingAgentQueryKeys.messages(
        actorId,
        organizationId,
        conversationId,
      ),
      queryFn: ({ pageParam }) => {
        required(actorId, 'actorId')
        return fetchReportingMessages({
          organizationId: required(organizationId, 'organizationId'),
          conversationId: required(conversationId, 'conversationId'),
          limit: 50,
          after: pageParam,
        })
      },
      enabled: Boolean(
        reportingEnabled && actorId && organizationId && conversationId,
      ),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    }),
  run: ({
    actorId,
    organizationId,
    runId,
    reportingEnabled = true,
  }: ReportingAgentQueryScope & {
    runId?: string
  }) =>
    queryOptions({
      queryKey: reportingAgentQueryKeys.run(actorId, organizationId, runId),
      queryFn: () => {
        required(actorId, 'actorId')
        return fetchReportingRun({
          organizationId: required(organizationId, 'organizationId'),
          runId: required(runId, 'runId'),
        })
      },
      enabled: Boolean(reportingEnabled && actorId && organizationId && runId),
    }),
  draftDisplay: ({
    actorId,
    organizationId,
    conversationId,
    artifactId,
    reportingEnabled = true,
  }: ReportingAgentQueryScope & {
    conversationId?: string
    artifactId?: string
  }) =>
    queryOptions({
      queryKey: reportingAgentQueryKeys.draftDisplay(
        actorId,
        organizationId,
        conversationId,
        artifactId,
      ),
      queryFn: () => {
        required(actorId, 'actorId')
        return fetchReportingDraftDisplay({
          organizationId: required(organizationId, 'organizationId'),
          conversationId: required(conversationId, 'conversationId'),
          artifactId: required(artifactId, 'artifactId'),
        })
      },
      enabled: Boolean(
        reportingEnabled &&
          actorId &&
          organizationId &&
          conversationId &&
          artifactId,
      ),
    }),
}
